import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import {html} from '@polymer/polymer/lib/utils/html-tag.js';
import {GestureEventListeners} from '@polymer/polymer/lib/mixins/gesture-event-listeners';
import template from './ns-adp-cart.html';
import {PriceType} from '@nuskin/ns-product-lib';
import {
  CartService,
  ServiceUtils,
  AdrService,
  CampaignService,
  ProductStatusService,
  ProductService,
  ShopContextService, Cart,
  CurrencyService,
  PersonalOfferService,
  Agelocme,
  AssessmentService
} from '@nuskin/ns-shop';
import {RunConfigService, dateFormatter, ConfigService, moment, storage, events, NsRestError} from '@nuskin/ns-util';
import {AccountManager, UserService} from '@nuskin/ns-account';
import {mockADR} from './mock-adr.js';
import modalCloseIcon from '../img/ic-close-small.svg';

import {retrieveProductionToggles} from '@nuskin/ns-feature-flags';
import { NsLoyalty } from '@nuskin/ns-loyalty';
import { MarketHeaderService } from "@nuskin/ns-product";
import { getConfiguration } from '@nuskin/configuration-sdk';

/**
 * Wrapper for the ADR Edit page that contains:
 * <ol>
 *  <li>ns-search component, for adding new items to the currently-selected ADR</li>
 *  <li>dom-repeat, displaying the line items in the current ADR</li>
 *  <li>adr totals component, showing the totals for the adr and the cancel/save buttons</li>
 *  <li>"return to adr manage" link</li>
 * </ol>
 *
 * <section>
 *  <h2>Content Guidelines</h2>
 *  <p>This component is dependent on the currently selected ADR number on local Storage.</p>
 * </section>
 *
 * <h2>ADP Cart Component Demo</h2>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-adp-cart is-demo></ns-adp-cart>
 *   </template>
 * </demo-snippet>
 */
export class NsAdpCart extends GestureEventListeners(PolymerElement) {
  static get is () {
    return 'ns-adp-cart';
  }

  static get template () {
    let htmlElement = document.createElement('TEMPLATE');
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  static get properties () {
    return {
      /**
       * translations
       * Label translations in the current language
       * @example:
       *   'en': {
       *      'returnToAdpManageLabel': 'Return to ADP Manage',
       *      'recurringItemsLabel': 'Recurring Automatic Delivery Items',
       *      ...
       *    }
       */
      translations: {
        type: JSON,
        reflectToAttribute: true
      },
      /**
       * country
       * Country code property
       * @default 'US'
       * @example 'DE'
       */
      country: {
        type: String
      },
      /**
       * language
       * Language code property
       * @default 'en'
       * @example 'jp'
       */
      language: {
        type: String
      },
      /**
       * currentADP
       * Currently selected ADP
       */
      currentADP: {
        type: Object
      },
      /**
       * isNotEmpty
       * Indicates whether to show the totals component or not
       * @example: true
       */
      isEmpty: {
        type: Boolean,
        value: false
      },
      /**
       * Used for tests to skip service calls and send mock objects
       */
      isTest: {
        type: Boolean
      },
      /**
       * Encoded value of the close icon used for passing this value to the template
       */
      modalCloseIcon: {
        type: String,
        value: modalCloseIcon
      },
      /**
       * Property to toggle service call loader, ns-global-loader component
       */
      showLoader: {
        type: Boolean
      },
      /**
       * The status of the redeem points checkbox.
       */
      redeemPointsChecked: {
        type: Boolean,
        value: false,
        observer: '_redeemPointsChanged'
      },
      returnToAdpManageOrCartLabel: {
        type: String,
        computed: 'computeReturnLabel(returnToCartLabel, returnToAdpManageLabel)'
      },
      /**
       * Populates component with test data and prevents any unnecessary network calls.
       */
      isDemo: {
        type: Boolean,
        value: false
      },
      /**
       * Page upon which this component appears. Used for translations.
       */
      webPageName: {
        type: String,
        value: 'shop/adp-edit'
      },
      /**
       * Translations from the server. Triggers the translation assignment process.
       * If undefined and shouldAwaitTranslations=false, this component will fetch its own strings.
       */
      serverTranslations: {
        type: Object,
        observer: '_assignTranslations'
      },
      /**
       * If false, it will start the translation assignment process without waiting.
       * If true, it will wait for serverTranslations to be set before starting the translation assignment process.
       * If serverTranslations is defined already, the component will behave as if this property is true.
       */
      shouldAwaitTranslations: {
        type: Boolean,
        value: false
      },

      /**
       * Controls whether or not ageloc codes should searchable. Based on the market's config
       */
      agelocMeInAdrProductSearch: {
        type: Boolean,
        value: true
      },

      /**
       * The original points balance which comes from adr service status call
       */
      originalPointsBalance: {
        type: Number,
        value: 0
      },
      /**
       * The last deleted line item, in case the user wants to undo that deletion,
       * and to add to the record of deleted items for the PUT call
       */
      lastItemDeleted: {
        type: Object,
        observer: '_deleteLineItem'
      },
      /**
       * cartInfo
       * Cart info returned from localStorage
       */
      cartInfo: {
        type: Array,
        value: []
      },
      /**
       * totalsInfo
       * Object containing totalCartPrice, availablePoints, and totalCartPsv
       */
      totalsInfo: {
        type: Object
      },
      /**
       * Common Constants used across file
       */
      _CONSTANTS: {
        type: Object,
        value: {
          BAD_REQUEST_CODE: '400',
          REFRESH_EXCEPTION: 'adrRefresh'
        }
      },
      /**
       * Counter binded to ns-line-item.
       * Displays number of purchase with points line-items
       */
      purchaseWithPointsCounter: {
        type: Number,
        value: 0
      },
      /**
       * Computed property label for purchaseWithPointsCounter
       * Label appears in <number-selected-tag> on template
       */
      purchaseWithPointsCounterComputedLabel: {
        type: String,
        computed: '_computePurchaseWithPointsCounterLabel(purchaseWithPointsCounter, selectedLabel)'
      },
      /**
       * List of deleted line items, for PUT call
       */
      deletedItems: {
        type: Array,
        value: []
      },
      /**
       * Cart length used for last adr item check
       */
      cartLength: {
        type: Number
      },
      /**
       * counter to keep track of the last adr item
       */
      oneTimeCounter: {
        type: Number,
        value: 0
      },
      /**
       * Selection for when to redeem points
       */
      shipWhen: {
        type: String,
        value: 'shipFuture',
        observer: 'shipWhenSelectionChanged'
      },
      /**
       * Search bar placeholder
       */
      searchbarPlaceholderText: {
        type: String,
        observer: '_searchbarPlaceholderTextChanged'
      },
      /**
       * Search bar cancel text
       */
      searchbarCancelText: {
        type: String,
        value: 'cancel', // default
        observer: '_searchbarCancelTextChanged'
      },
      /**
       * Search bar translations object. Can be found in ns-searchbar-legacy.js
       */
      searchBarPlaceholderTranslations: {
        type: Object
      },
      /**
       * Collection of toast message objects, shared with child components.
       * Messages in this collection are displayed by this component.
       */
      toastMessages: {
        type: Array,
        value: () => []
      },
      /**
       * Computed property to set up minAdrPsvModalText with the appropriate minAdrPsv value
       */
      minAdrPsvModalTextComputed: {
        type: String,
        computed: 'computeMinAdrPsvModalText(minAdrPsvModalText, minAdrPsv)'
      },

      removedItems: {
        type: Boolean,
        value: false
      },

      loyaltyPointsUsed: {
        type: Boolean,
        computed: '_computeUsingPoints(currentADP)'
      },

      adjustPoints: {
        type: Boolean,
        computed: '_computeAdjustPoints(removedItems, loyaltyPointsUsed)'
      },
      /**
       * List of available coupons to apply to the ADR
       */
      availableCoupons: {
        type: Array,
        value: () => []
      },
      /**
       * Label for button to open the list of available coupons
       */
      openCpnListBtnTxt: {
        type: String
      },
      isCpnCollapsibleOpen: {
        type: Boolean,
        value: false
      },
      isPromoCpnSummaryHidden: {
        type: Boolean,
        value: true
      },
      isCpnMainMenuDropdownHidden: {
        type: Boolean,
        value: false
      },
      isCpnMainMenuHidden: {
        type: Boolean,
        value: false
      },
      isCpnListHidden: {
        type: Boolean,
        value: true
      },
      isCpnListEmptyComputed: {
        type: Boolean,
        computed: '_computeIsCpnListEmpty(availableCoupons)'
      },
      isCpnListSelectedCpnHidden: {
        type: Boolean,
        value: true
      },
      isApplyListCpnBtnDisabled: {
        type: Boolean,
        value: true
      },
      isPsvTotalHidden: {
        type: Boolean,
        value: true
      },
      /**
       * Computed label for button to open the list of available coupons
       */
      openCpnListBtnTxtComputed: {
        type: String,
        computed: '_computeOpenCpnListBtnTxt(openCouponListButtonLabel, availableCoupons)'
      },
      /**
       * Display format for coupon expiration dates, as MomentJS format
       */
      couponExpDateFormat: {
        type: String,
        value: 'M/D/YYYY'
      },
      selectedCpnCode: {
        type: String,
        value: ''
      },
      productPromoSavings: {
        type: Number,
        value: 0
      },
      shippingPromoSavings: {
        type: Number,
        value: 0
      },
      isSaveBtnDisabled: {
        type: Boolean,
        value: false
      },
      hasCommittedChange: {
        type: Boolean,
        value: false
      },
      useCustomAgelocMeLabel: {
        type: Boolean,
        value: false
      }
    };
  }

  static get observers () {
    return [
      '_onToastMessagesChanged(toastMessages.splices)',
      '_handleModals(modalsToResolve.splices)',
      '_setMinimumGrandfatherPricing(totalsInfo.totalCartPrice, totalsInfo.totalCartWholeSalePrice, cartLength)'
    ];
  }

  constructor () {
    super();
    this.featureFlags = {};
    const userId = UserService.isLoggedIn()
      ? UserService.getUser().id
      : undefined;
    retrieveProductionToggles(userId).then(toggles => {
      const featureFlags = Array
        .from(toggles)
        .reduce((a, x) => Object.assign(a, {[x]: true}), {});
      this.set('featureFlags', featureFlags);
    })
    $('body').hide();
  }

  /**
  * Polymer life cycle event
  * Pulls all translations for labels on the page and calls initial functions.
  */
  ready () {
    super.ready();
    if (AccountManager.isLoggedIn() === null && !this.isDemo) {
      events.publish(events.authentication.REQUEST_FOR_LOGIN);
    } else {
      $('body').show();
    }
    if (this.isTest) {
      storage.setItem(storage.metadata.ADR_DETAIL, JSON.stringify(mockADR), {});
    }
    this.hideOverlay = true;
    this.redeemPointsChecked = false;

    const urlParams = new URLSearchParams(window.location.search);
    this.shopCartFlag = urlParams.has('add') && urlParams.get('add') === 'cart';

    this._setConfigurations();
    this.promoCode = {};
    this.shipCode = {};

    if (!this.isDemo) {
      getConfiguration(['Cart', 'Loyalty']).then(() => {
        this._getCartInfo().then(() => {
          if (this.enablePayWithPoints) return this._initUserPointsBalance();
        }).catch(console.error);
      }).catch(console.error);
    } else {
      this._getTestCartInfo();
    }

    this._initializeRedeemPointsCheckbox();
    let context = this;
    events.subscribe(events.shop.SEARCH_TYPEAHEAD, function(result) {
      if (result.item) {
          const product = result.item;
          context._handleNewProduct(product);
      }
    });
    events.subscribe(events.shop.CART_ITEM_UPDATED, function(eventData) {
      context._oneTimeChangeHandler(eventData.item, eventData.index)
    });
  }

  /**
   * Sets configurations to customize functionality for the current market
   */
  _setConfigurations () {
    const RUN_CONFIG = RunConfigService.getRunConfig();
    const SERVICE = ConfigService.getMarketConfig();
    const DEFAULT = {
      'adpManage' : {
        'showProgramRulesLink': false,
        'linkToProgramRules': '',
        'enablePayWithPoints': false,
        'showPromoSection': false,
        'agelocMeInAdrProductSearch': true,
        'cartMinimumtoAvoidGrandfatherDialog': 0,
        'agelocMeCustomLabelInEditManage': false,
        'useWholeSalePriceforGrandfatherDialog': false,
        'allowVitaMealOnlyGrandfatherPricing': false,
        'vitaMealSku': ''
      },
      'adrProgram': 'ARO'
    };
    const CONFIG = Object.assign({}, DEFAULT, SERVICE);

    this.adrProgramName = CONFIG.adrProgram;
    this.enablePayWithPoints = CONFIG.adpManage.enablePayWithPoints;
    this.useShippingCountryAdrPoints = CONFIG.adpManage.useShippingCountryAdrPoints || false;
    this.adrPointsCountryOverride = CONFIG.adpManage.adrPointsCountryOverride;
    this.showProductDiscountCode = CONFIG.adpManage.showProductDiscountCode;
    this.showShippingDiscountCode = CONFIG.adpManage.showShippingDiscountCode;
    this.disableItemDeletion = CONFIG.adpManage.disableItemDeletion;
    this.disableItemQuantityDecrease = !!CONFIG.adpManage.disableItemQuantityDecrease;
    this.agelocMeInAdrProductSearch = CONFIG.adpManage.agelocMeInAdrProductSearch;
    this.country = this.country || RUN_CONFIG.country;
    this.language = this.language || RUN_CONFIG.language;
    this.minAdrPsv = CONFIG.minAdrPsv;
    this.minAdrPsvCheck = CONFIG.minAdrPsvCheck || 'none';
    this.shipOneTimeImmediateOrderType = CONFIG.adpManage.shipOneTimeImmediateOrderType;
    this.cartMinimumtoAvoidGrandfatherDialog = CONFIG.adpManage.cartMinimumtoAvoidGrandfatherDialog;
    this.showPaymentPromoCodeOffers = !!CONFIG.checkout.showPaymentPromoCodeOffers;
    const allowSRII = CONFIG.adpManage.allowShipRedeemedItemsImmediately;
    this.allowShipRedeemedItemsImmediately = (allowSRII === undefined) ? true : allowSRII; // If config setting is absent, default to `true`
    this.useCustomAgelocMeLabel = CONFIG.adpManage.agelocMeCustomLabelInEditManage;
    this.useWholeSalePriceforGrandfatherDialog = CONFIG.adpManage.useWholeSalePriceforGrandfatherDialog;
    this.allowVitaMealOnlyGrandfatherPricing = CONFIG.adpManage.allowVitaMealOnlyGrandfatherPricing;
    this.vitaMealSku = CONFIG.adpManage.vitaMealSku;

    switch(CONFIG.adrProgram) {
      case 'ARO':
        this.adrIcon = 'https://test.nuskin.com/global/images/general/aro_logo_small.gif';
        this.showAdrIcon = true;
        break;
      case 'ADR':
        this.adrIcon = 'https://www.nuskin.com/content/dam/global/images/logo-adr.svg';
        this.showAdrIcon = true;
        break;
      default:
        this.adrIcon = '';
        this.showAdrIcon = false;
    }
  }

  _setMinimumGrandfatherPricing() {
    //check to see if it's possible that this subscription could become vitameal only and the market allow vitameal only exception
    if (!this.totalsInfo) {
      return;
    }
    let vitaMealOnlyPossible = false;
    if (this.allowVitaMealOnlyGrandfatherPricing && this.vitaMealSku) {
      if (this.cartLength == 2 && this.cartInfo.some(x => x.SKU == this.vitaMealSku)) {
        vitaMealOnlyPossible = true;
      }
    }
    if (this.cartMinimumtoAvoidGrandfatherDialog && this.cartMinimumtoAvoidGrandfatherDialog > 0) {
      let maxToAvoidDialog = this.totalsInfo.totalCartPrice - this.cartMinimumtoAvoidGrandfatherDialog;
      if (this.useWholeSalePriceforGrandfatherDialog) {
        maxToAvoidDialog = this.totalsInfo.totalCartWholeSalePrice - this.cartMinimumtoAvoidGrandfatherDialog;
      }
      if (maxToAvoidDialog > 0) {
        this.cartInfo.forEach((item, index) => {
          let totalLinePrice = this.useWholeSalePriceforGrandfatherDialog ?
            item.totalLineItemWholesalePrice : item.totalLineItemPrice;
          if (totalLinePrice > maxToAvoidDialog) {
            this.set(`cartInfo.${index}.GrandFatheredPricing`, true);
          } else {
            this.set(`cartInfo.${index}.GrandFatheredPricing`, false);
          }
          // if vitameal only would happen on removing this product, override the grandfather price
          if (vitaMealOnlyPossible && item.SKU !== this.vitaMealSku) {
            this.set(`cartInfo.${index}.GrandFatheredPricing`, false)
          }
          return item;
        })
      } else {
        this.cartInfo.forEach((item, index) => {
          this.set(`cartInfo.${index}.GrandFatheredPricing`, false);
        })
      }
    }
  }

  /**
   * Observer on serverTranslations that gathers all translations and maps them to properties.
   * Any strings set as properties and defined in the html attributes take precedence over translations from the server,
   * which in turn take precedence over the default translations in the translations file.
   */
  async _assignTranslations () {
    let allTranslations = Object.assign({}, this.serverTranslations);
    Object.entries(allTranslations).forEach(([key, value]) => this[key] = this[key] || value);

    if(allTranslations.searchbarCancelButtonText) this.set('searchbarCancelText', allTranslations.searchbarCancelButtonText)
  }

  /**
   * Computes the min adr psv modal text to set up and replace the minPsv placeholder with the minAdrPsv value
   * @param {*} minAdrPsvModalText
   * @param {*} minAdrPsv
   */
  computeMinAdrPsvModalText (minAdrPsvModalText, minAdrPsv) {
    if (minAdrPsvModalText && minAdrPsv) {
      return minAdrPsvModalText.replace(/({minPsv})/g, this.minAdrPsv);
    }
  }

  /**
   * Computes which label to show on the "return to..." button
   */
  computeReturnLabel (cartLabel, manageLabel) {
    return this.shopCartFlag ? cartLabel : manageLabel;
  }

  /**
   * Pulls ADR object from Server, option.fullAdrDetail tells AdrService to cache full GET adrDetails response.
   * adrId in storage must be set to populate ADR Cart, when not present
   */
  _getCartInfo () {
    this.adrId = storage.getItem(storage.metadata.ADR_CURRENT_ID);

    // Send to error page. Currently the 'No Order' error-page, but when a generic error-page is made we should redirect there.
    if (!this.adrId || this.adrId === 'undefined') this._errorRedirect();

    const options = {forceRefresh: false, fullAdrDetail: true};
    return AdrService.getAdrDetails(this.adrId, options)
      .then(results => {
        this.currentADP = results;
        this.set('promoCode.input', this.currentADP.SalesOrderDetail.ProductDiscountCode);
        this.set('shipCode.input', this.currentADP.SalesOrderDetail.ShippingDiscountCode);

        if (this.showPaymentPromoCodeOffers) {
          if (this.promoCode.input) {
            this._initExistingCouponData();
          } else {
            this._initAvailableCoupons();
          }
        }

        // We don't want to show child products (i.e., those with nonzero ParentID).
        const cartInfo = this.currentADP.SalesOrderDetail.LineItemDetails
          .filter(item => item.LineItem.ParentID === 0)
          .map(item => ({
            SKU: item.LineItem.SKU,
            LineID: item.LineItem.LineID,
            ParentID: item.LineItem.ParentID,
            RequestedQuantity: item.LineItem.RequestedQuantity,
            OneTime: item.LineItem.OneTime,
            PayWithPoints: item.LineItem.PayWithPoints,
            GrandFatheredPricing: item.GrandFatheredPricing
          }));
        let initialCart = cartInfo;
        initialCart.forEach(item => {item.OneTime = item.PayWithPoints || item.OneTime;});
        this.initialCart = JSON.parse(JSON.stringify(initialCart));

        if (this.shopCartFlag) {
          const cartProducts = CartService.getItemData({cartItems: true});
          cartProducts.forEach((product) => {
            const sku = product.sku;
            const oneTimeAdrCheckIndex = cartInfo.findIndex((lineItem) => sku === lineItem.SKU && (product.isAdr !== lineItem.OneTime));
            if (oneTimeAdrCheckIndex > -1) {
              // Item from cart is already in ADR; update item quantity
              cartInfo[oneTimeAdrCheckIndex].RequestedQuantity += product.qty;
            } else {
              // Item from cart is not already in ADR; add as new item
              cartInfo.push({
                SKU: product.agelocme ? product.agelocme.code : product.sku,
                RequestedQuantity: product.qty,
                OneTime: !product.isAdr,
                PayWithPoints: null,
                GrandFatheredPricing: null,
                LineItemNewStatus: true
              });
            }
          });
        }

        this.nextOrderDateString = dateFormatter.simpleDate(this.currentADP.NextDeliveryDate);
        const AGELOCME_SKU = ConfigService.getMarketConfig().agelocme.sku;
        const promises = cartInfo
          .map(lineItem => /^[0-9]+$/.test(lineItem.SKU) ? lineItem.SKU : AGELOCME_SKU)
          .map(sku => ProductService.getProductBySku({
            sku,
            cntryCd: this.country,
            language: this.language
          }))
          .map(p => p.catch(err => {
            console.error('ProductService error:', err);
            return null;
          }));

        Promise.all(promises).then(async responses => {
          if (!this.assessments) {
            await this._getAgelocMeAssessments()
          }
          responses.forEach((response, idx) => {
            if (response && response.product.sku === AGELOCME_SKU) {
              response.product.sku = cartInfo[idx].SKU;
              this.assessments.filter(assessment => {
                return assessment.code == cartInfo[idx].SKU;
              }).map(match => {
                response.product.agelocme = new Agelocme({
                  code: match.productCode || match.code,
                  isCoreSolution: match.isCoreSolution,
                  label: match.profileName || match.label,
                  name: match.name
                });
              })
            }
          });
          responses = responses.filter(r => r);

          const cart = this._setLineItemProperties(cartInfo, responses);

          this.cartLength = cart.cartInfo.length;
          this.cartInfo = cart.cartInfo;
          this.totalsInfo = cart.totalsInfo;
          this.originalPointsBalance = cart.pointsBalanceWithoutAdr;

          /*
           * if there is one sku that is already pay with points, then click the redeem points checkbox by default
           */
          if (this.cartInfo.some(lineItem => lineItem.PayWithPoints)) this.redeemPointsChecked = true;
        })
        .catch(err => {
          this._handleError(err);
        });
    })
  }

  /**
   * Calculates totals for Price Points and PSV
   * @param {[LineItems]} [cartInfo] - Array of line-items created by items on ADR and shop-cart items if applicable
   * @param {*} products
   * @returns {Object} - modified cartInfo and totals object
   */
  _setLineItemProperties(cartInfo, products) {
    let cartTotalPoints = this.originalPointsBalance || 0;
    let cartTotalPSV = 0;
    let cartTotalPrice = 0;
    let cartTotalWholesalePrice = 0;

    // TODO: use product.js to determine special event pricing, since this assumes there is none:
    const priceType = ShopContextService.getPriceType(true);

    const wholeSalePriceType = PriceType.WHL;

    // Flag to indicate whether one or more line items are missing pricing data
    let isPriceMissing = false;
    let countProductsWithPsv = 0;

    cartInfo.forEach((lineItem) => {
      const SKU = lineItem.SKU;
      const PayWithPoints = lineItem.PayWithPoints;
      if (PayWithPoints) {
        this.purchaseWithPointsCounter++;
        // SAP can't save both PayWithPoints and OneTime at the same time so it's handled here
        lineItem.OneTime = true;
      }
      if (lineItem.OneTime) this.oneTimeCounter++;
      const index = products.findIndex(product => SKU === product.product.sku);
      let title;
      if (products[index].product.agelocme && this.useCustomAgelocMeLabel) {
        title = this._createAgelocCustomLabel(products[index].product)
      } else {
        title = products[index].product.title
      }
      lineItem.availableQty = products[index].product.availableQuantity;
      lineItem.isOverPointsBalance = false;
      lineItem.isLastAdrItem = false;
      lineItem.image = products[index].product.fullImage || products[index].product.thumbnail;
      lineItem.imageAlt = products[index].product.imageAltText || title;
      lineItem.title = title;
      lineItem.productDetails = products[index].product.longDescr || products[index].product.shortDescr || '';
      lineItem.isHidden = false;
      lineItem.isDiscontinued = products[index].product.status == "DISCONTINUED";
      const points = products[index].product.points;
      const psv = products[index].product.pvMap[priceType];
      const price = products[index].product.priceMap[priceType];
      const wholeSalePrice = products[index].product.priceMap[wholeSalePriceType];
      const RequestedQuantity = lineItem.RequestedQuantity;

      if (!Number.isFinite(price)) isPriceMissing = true;

      if (RequestedQuantity) {

        if (points && PayWithPoints) {
          cartTotalPoints = (cartTotalPoints * 100 + points * 100 * RequestedQuantity) / 100;
          const pointsTotal = points * RequestedQuantity;
          lineItem.totalLineItemCostInPoints = pointsTotal;
          lineItem.maxNumber = Math.floor((this.cartTotalPoints * 100) / (points * 100));

          lineItem.totalLineItemPsv = 0;
          lineItem.totalLineItemPrice = 0;
          lineItem.totalLineItemWholesalePrice = 0;
        } else {
          lineItem.maxNumber = 99;
          // some markets do not have points
          if (psv) {
            cartTotalPSV = (cartTotalPSV * 100 + psv * 100 * RequestedQuantity) / 100;
            const psvTotal = psv * RequestedQuantity;
            lineItem.totalLineItemPsv = psvTotal;
          }

          cartTotalPrice = cartTotalPrice + price * RequestedQuantity;
          cartTotalWholesalePrice = cartTotalWholesalePrice + wholeSalePrice * RequestedQuantity;
          lineItem.totalLineItemPrice = price * RequestedQuantity;
          lineItem.totalLineItemWholesalePrice = wholeSalePrice * RequestedQuantity;
        }

        lineItem.maxQuantity = lineItem.maxNumber;

        lineItem.disableDeletion = !!this.disableItemDeletion;

        if (this.disableItemQuantityDecrease) {
          lineItem.minQuantity = lineItem.RequestedQuantity;
        }

        lineItem.unitCostInPoints = points;

        // Set unit PSV to null if no PSV was found for product.
        // null value is used to indicate to ns-line-item that product has no PSV.
        lineItem.unitPsv = isFinite(psv) ? psv : null;
        if (isFinite(psv)) countProductsWithPsv++;

        if (price) {
          lineItem.unitPrice = price;
        }

        if (wholeSalePrice) {
          lineItem.unitWholeSalePrice = wholeSalePrice;
        }

        if (points * RequestedQuantity > this.originalPointsBalance) {
          lineItem.isOverPointsBalance =  true;
        }
      }
    });

    if (isPriceMissing && this.priceMissingErrorMessage) {
      // Delay applied here because of timing conflicts with toast message array initialization
      setTimeout(() => {
        this._addToastMessage('error', {
          header: this.priceMissingErrorMessage,
          scrollIntoView: true
        });
      }, 500);
    }

    this.isPsvTotalHidden = countProductsWithPsv === 0;

    /**
     * If there is only one line item remaining that is recurring and not redeemed with points,
     * set "isLastAdrItem" flag on that item.
     */
    const filteredItems = cartInfo.filter(lineItem => !lineItem.OneTime && !lineItem.PayWithPoints);
    if (filteredItems.length === 1) filteredItems.forEach(item => item.isLastAdrItem = true);

    return {
      cartInfo: cartInfo,
      pointsBalanceWithoutAdr: cartTotalPoints || 0,
      totalsInfo: {
        availablePoints: this.originalPointsBalance || 0,
        totalCartPrice: cartTotalPrice,
        totalCartPsv: cartTotalPSV,
        totalCartWholeSalePrice: cartTotalWholesalePrice
      }
    };
  }

  _redeemPointsChanged () {
    if (!this.redeemPointsChecked) {
      this._clearToastMessages();
      this.set('totalsInfo.availablePoints', this.originalPointsBalance);
      this.shipWhen = "shipFuture"
    }
  }

  /**
   * Function that redirects to the adp error page
   */
  _errorRedirect() {
    const adpErrorPage = `${RunConfigService.getBaseUrl()}/${RunConfigService.getRunConfig().language}_${RunConfigService.getRunConfig().country}/shop/adp-error.html`;
    window.location = adpErrorPage;
  }

  _getTestCartInfo () {
    let cartInfo = mockADR.SalesOrderDetail.LineItemDetails.map((item) => {
      return {
        SKU: item.LineItem.SKU,
        LineID: item.LineItem.LineID,
        ParentID: item.LineItem.ParentID,
        RequestedQuantity: item.LineItem.RequestedQuantity,
        OneTime: item.LineItem.OneTime,
        PayWithPoints: item.LineItem.PayWithPoints,
        GrandFatheredPricing: item.GrandFatheredPricing,
        Action: '',
        maxNumber: 99
      };
    });
    this.cartInfo = cartInfo;
    this.nextOrderDateString = dateFormatter.simpleDate(cartInfo.NextDeliveryDate);
  }

  /**
   * Handles the product selected by the user in the search bar or undoes the last line item deletion.
   * If a searched product already exists in the cart, it just increases its quantity instead of adding a new line item.
   * @param {object} product - the product info from the search results
   */
  _handleNewProduct (product) {
    this._clearToastMessages();
    if (product) {
      const index = this._findMatchingProduct(product);
      if (index > -1) {
        const oldQty = this.cartInfo[index].RequestedQuantity;
        const maxQty = this.cartInfo[index].maxQuantity;
        const newQty = Math.min(oldQty + 1, maxQty);
        this.set(`cartInfo.${index}.RequestedQuantity`, newQty);
        // trigger the view to scroll to that line and highlight it:
        this.set(`cartInfo.${index}.justUpdated`, true);
      } else {
        this._addLineItem(product);
      }
    }
  }

  /**
   * Adds a new line item to the cart from the search bar
   * @param {Object} product - item to add to this.cartInfo
   */
  _addLineItem (product) {
    const productSKU = product.agelocme && product.agelocme.code? product.agelocme.code: product.sku;
    let title;
    if (product.agelocme && this.useCustomAgelocMeLabel) {
      title = this._createAgelocCustomLabel(product)
    } else {
      title = product.title
    }
    let newProduct = {
      imageAlt: product.title,
      title: title,
      SKU: product.sku,
      productDetails: product.longDescr || product.shortDescr || '',
      image: product.fullImage,
      RequestedQuantity: 1,
      OneTime: false,
      PayWithPoints: false,
      GrandFatheredPricing: false,
      lineItemNewStatus: true,
      isHidden: false,
      isNew: true,
      NewItemLineID: Date.now()
    };
    this._getProductStatus(newProduct, () => {
      const index = this.cartInfo.length;
      //need the product sku to be set last
      newProduct.SKU = productSKU
      this.splice('cartInfo', index, 0, newProduct);
      // Force-render the new item so scrolling to it will work
      this.shadowRoot.querySelector('dom-repeat').render();
      this._updateTotals(index);
      this.set('cartLength', this.cartLength + 1);
    });
  }

  /**
   * Retrieves new line item price, psv, points, and available quantity from ProductStatusService
   * @param {Object} newProduct - the new line item info
   * @param callback() - waits for pricing before continuing _addLineItem()
   */
  _getProductStatus(newProduct, callback){
    const priceType = ShopContextService.getPriceType(true);
    const wholeSalePriceType = PriceType.WHL;
    ProductStatusService.getProductStatus(newProduct.SKU, this.country)
      .then(status => {
        newProduct.unitPrice = (status[0].price[priceType] || 0);
        newProduct.unitPsv = (status[0].psv || {})[priceType] || null;
        newProduct.unitWholeSalePrice = (status[0].price[wholeSalePriceType] || 0)
        this.isPsvTotalHidden &= newProduct.unitPsv === null;
        newProduct.unitCostInPoints = (status[0].price.PTS || 0);
        newProduct.availableQty = status[0].availableQuantity || 0;
        callback();
      })
      .catch(err => {
        this._handleError(err);
      });
  }

  /**
   * Updates cart totals when adding new line items
   * @param {Number} i - the new line item's index in the cartInfo object
   */
  _updateTotals (i) {
    const item = this.cartInfo[i];
    let maxNum = 99;
    if (item.PayWithPoints) {
      maxNum = item.unitCostInPoints != 0 ? this.totalsInfo.availablePoints / item.unitCostInPoints : maxNum;
    }
    this.set(`cartInfo.${i}.maxQuantity`, maxNum);

    this.set('totalsInfo.totalCartPsv', ((this.totalsInfo.totalCartPsv * 100 +
      (item.RequestedQuantity * item.unitPsv * 100)) / 100));
    this.set('totalsInfo.totalCartPrice', ((this.totalsInfo.totalCartPrice * 100 +
      (item.RequestedQuantity * item.unitPrice * 100)) / 100));
    this.set('totalsInfo.totalCartWholeSalePrice', ((this.totalsInfo.totalCartWholeSalePrice * 100 +
      (item.RequestedQuantity * item.unitWholeSalePrice * 100)) / 100));

    this.set(`cartInfo.${i}.totalLineItemPsv`, ((item.RequestedQuantity * item.unitPsv * 100) / 100));
    this.set(`cartInfo.${i}.totalLineItemPrice`, ((item.RequestedQuantity * item.unitPrice * 100) / 100));
    this.set(`cartInfo.${i}.totalLineItemWholesalePrice`, ((item.RequestedQuantity * item.unitWholeSalePrice * 100) / 100));
    this.set(`cartInfo.${i}.justUpdated`, true);
  }

  /**
   * Finds a product that matches the provided product
   * @param {Object} product - The product to match.
   */
  _findMatchingProduct (product) {
    if (product) {
      let index = this.cartInfo.findIndex((item, index) => {
        if (index == product.itemsindex) { return false; }
        const isAgelocme = (product.agelocme && product.agelocme.code)
          || !/^[0-9]+$/.test(product.SKU);
        let skusMatch = item.SKU === product.sku || item.SKU === product.SKU;
        if (!skusMatch && isAgelocme) {
          skusMatch = product.agelocme && product.agelocme.code === item.SKU;
        }
        const oneTimesMatch = !!item.OneTime == !!product.OneTime;
        return skusMatch && oneTimesMatch && !item.isHidden;
      });
      return index;
    }
    return -1;
  }

  /**
   *
   * @param {*} item - The item which triggered the change.
   * @returns
   */
  _oneTimeChangeHandler (item) {
    const itemIdx = item.itemsindex;
    // find another line item that matches this line item for SKU and one-time status
    const otherItemIdx = this._findMatchingProduct(item);

    // if no other matching line item is found, just toggle the one-time flag for this item
    if (otherItemIdx === -1) {
      return item.OneTime;
    }

    // the lower-index line item will be the one to keep; merge both line items' quantities into it
    const idxToKeep = Math.min(itemIdx, otherItemIdx);
    const idxToDiscard = Math.max(itemIdx, otherItemIdx);
    const itemToKeep = itemIdx === idxToKeep ? item : this.cartInfo[idxToKeep]
    const itemToDiscard = itemIdx === idxToDiscard ? item : this.cartInfo[idxToDiscard];
    const combinedQty = this.cartInfo[otherItemIdx].RequestedQuantity + (item.RequestedQuantity || 1);
    this.set(`cartInfo.${idxToKeep}.RequestedQuantity`, Math.min(combinedQty, itemToKeep.maxQuantity));
    this.set(`cartInfo.${idxToKeep}.justUpdated`, true);

    // mark the other line item as deleted and update the cart data accordingly
    this.set(`cartInfo.${idxToDiscard}.isHidden`, true);
    if (itemToDiscard.PayWithPoints && itemToDiscard.unitCostInPoints) {
      this.set('totalsInfo.availablePoints', this.totalsInfo.availablePoints + itemToDiscard.RequestedQuantity * itemToDiscard.unitCostInPoints);
      this.set('purchaseWithPointsCounter', this.purchaseWithPointsCounter - 1);
    } else {
      this.set('totalsInfo.totalCartPsv', this.totalsInfo.totalCartPsv - itemToDiscard.RequestedQuantity * itemToDiscard.unitPsv);
      this.set('totalsInfo.totalCartPrice', this.totalsInfo.totalCartPrice - itemToDiscard.RequestedQuantity * itemToDiscard.unitPrice);
      this.set('totalsInfo.totalCartWholeSalePrice', this.totalsInfo.totalCartWholeSalePrice - itemToDiscard.RequestedQuantity * itemToDiscard.unitWholeSalePrice);
    }

    if (itemToDiscard.OneTime) {
      this.set('oneTimeCounter', this.oneTimeCounter - 1);
    }

    const deletedLineItem = {
      SKU: itemToDiscard.SKU,
      LineID: itemToDiscard.LineID,
      NewItemLineID: itemToDiscard.newItemLineId,
      ParentID: itemToDiscard.ParentID,
      PromoCode: itemToDiscard.PromoCode,
      Custom: itemToDiscard.Custom,
      Pricing: itemToDiscard.Pricing,
      title: itemToDiscard.title,
      unitPrice: itemToDiscard.unitPrice,
      unitPsv: itemToDiscard.unitPsv,
      unitCostInPoints: itemToDiscard.unitCostInPoints,
      productDetails: itemToDiscard.productDetails,
      image: itemToDiscard.image,
      imageAlt: itemToDiscard.imageAlt,
      RequestedQuantity: itemToDiscard.RequestedQuantity,
      OneTime: itemToDiscard.OneTime,
      PayWithPoints: itemToDiscard.PayWithPoints,
      GrandfatheredPricing: itemToDiscard.GrandfatheredPricing
    };
    this.push('deletedItems', deletedLineItem);
    this.set('cartLength', this.cartLength - 1);

    return itemIdx === idxToKeep ? item.OneTime : null;
  }

  /**
   * Removes a line item from the dom-repeat because the user clicked its delete button
   * Saves its index in the cart first and put its product info in the list of deleted items
   */
  _deleteLineItem () {
    if (this.lastItemDeleted) {
      this.deletedItems.push(this.lastItemDeleted);
      this.set('cartLength', this.cartLength - 1);
      // Show the user a toast allowing undo-ing of this deletion
      this._addToastMessage('success', {
        header: `${this.lastItemDeleted.title}${['zh', 'ja', 'th'].includes(this.language) ? '' : ' '}${this.toastDeleteMessage}`,
        showUndoOption: true
      });
    }
  }

  /**
   * Observer for the undo button on the ns-toast after removing a cart item.
   * Adds the recently deleted item back into the cart.
   */
  _undoDelete () {
    let undoneDeletedItem = this.deletedItems.pop();
    const oldIndex = this.cartInfo.findIndex(lineItem =>
      lineItem.SKU === undoneDeletedItem.SKU && lineItem.OneTime === undoneDeletedItem.OneTime);
    this.set('lastItemDeleted', null);
    this.set(`cartInfo.${oldIndex}.isHidden`, false);
    this.set(`cartInfo.${oldIndex}.justUpdated`, true);
    // to keep the state of totals consistent when undo is clicked
    if (undoneDeletedItem.PayWithPoints && undoneDeletedItem.unitCostInPoints) {
      this.set('totalsInfo.availablePoints', ((this.totalsInfo.availablePoints * 100 - (undoneDeletedItem.RequestedQuantity * undoneDeletedItem.unitCostInPoints * 100)) / 100));
      this.set('purchaseWithPointsCounter', this.purchaseWithPointsCounter + 1);
    } else {
      this.set('totalsInfo.totalCartPsv', ((this.totalsInfo.totalCartPsv * 100 + (undoneDeletedItem.RequestedQuantity * undoneDeletedItem.unitPsv * 100)) / 100));
      this.set('totalsInfo.totalCartPrice', ((this.totalsInfo.totalCartPrice * 100 + (undoneDeletedItem.RequestedQuantity * undoneDeletedItem.unitPrice * 100)) / 100));
      this.set('totalsInfo.totalCartWholeSalePrice', ((this.totalsInfo.totalCartWholeSalePrice * 100 + (undoneDeletedItem.RequestedQuantity * undoneDeletedItem.unitWholeSalePrice * 100)) / 100));
    }

    if (undoneDeletedItem.OneTime) {
      this.set('oneTimeCounter', this.oneTimeCounter + 1);
    }
    this.set('cartLength', this.cartLength + 1);
    this._clearToastMessages();
  }

  /**
   * Function that is called on-tap of cancel and returnToAdpManage buttons.
   * Returns user to previous page, ADP Manage or Checkout Cart page.
   */
  _returnToADPManageOrCart () {
    if (this.isDemo) {
      return;
    }

    let _configureBaseURL = page => {
      return `${RunConfigService.getBaseUrl()}/${RunConfigService.getRunConfig().language}_${RunConfigService.getRunConfig().country}${page}`;
    };
    let _configureExtension = pagePath => {
      return pagePath.endsWith('.html') ? pagePath : `${pagePath}.html`
    }

    let adpManagePage = this.adpManagePath ? _configureExtension(this.adpManagePath) : _configureBaseURL('/shop/adp-manage.html');

    if (!this.shopCartFlag && this.hasCommittedChange) {
      sessionStorage.setItem('has-adr-cart-committed-change', 'true');
    }

    window.location = this.shopCartFlag ? CartService.getCartPage() : adpManagePage;
  }

  /**
   * Function that is called on-tap of save changes button.
   * Calls checkForModals fn to determine if warning modals need to be shown.
   * If there are modals that need to be displayed, calls the _handleModals function
   * Otherwise, submit changes.
   */
  async _callSaveChanges (event, eventDetail) {
    if (this.isSaveBtnDisabled) return;
    if (this.isDemo) return;
    const checksPassed = await this._checkScanCardsAndPoints();
    this.hasModalsToDisplay = this.modalsToResolve.length > 0;
    console.log(`Checks passed: ${checksPassed}; has modals: ${this.hasModalsToDisplay}`);
    if (this.hasModalsToDisplay) {
      this._handleModals();
    } else if (checksPassed) {
      /*
       * Apart from the checks concerned with scan card and pay-with-points options,
       * the above checks can fail on a failed simulate order call. The update
       * should only be submitted if all checks and the simulate call pass.
       */
      this.modalsToResolve = []; // Reset modals array.

      const skus = this.cartInfo.filter(i => i.isNew).map(i => i.SKU);
      const adrId = this.currentADP.SalesOrderDetail.OrderID;
      // Set flag indicating that this ADR has been updated
      sessionStorage.setItem('adr-new-items', JSON.stringify({
        adrId,
        skus
      }));
      const veraData = storage.getItem(storage.metadata.SKINCONSULT_VUEX_STATE)
      if(nuskin.util.isSkinConsult && nuskin.util.isSkinConsult() && veraData && veraData.app && veraData.app.agreedToProgressUpdateShare) {
        storage.removeItem(storage.metadata.SKINCONSULT)

        // update status
        PersonalOfferService.callVeraPurchase(nuskin.util.getSponsorId())
      }
      this._submitChanges();
    }
  }

  /**
   * Checks if there are warning modals to display to the user.
   * Undergoes checks for multiple scenarios i.e. minAdrPsv, no product selected to redeem with points
   * @returns {Promise<boolean>} Promise that resolves `true` if checks succeed and `false` if they fail
   */
  async _checkScanCardsAndPoints () {
    this.hasModalsToDisplay = false;
    this.modalsToResolve = [];
    if (this._isMinAdrNotMetAndBlocked()) return true; // Bypass remaining modal checks and continue to submit changes.
    const scanCardsOkay = await this._checkScanCards();
    const pointsOkay = this._checkPayWithPoints();
    return scanCardsOkay && pointsOkay;
  }

  /**
   * Checks if minimum adr psv needs to be checked per market config
   * options are: 'none', 'warning', or 'block')
   * if minAdrPsvCheck is set to 'block' and the user has not met the min adr psv, we want to continue to
   * submit the changes. The service will throw an error and we can obtain the localized error
   * message from the response to display for the user.
   * if minAdrPsvCheck is set to 'warning and the user has not met the min adr psv set by the market,
   * a minAdrPsv modal needs to be displayed.
   * @returns {Boolean} to determine if it needs to bypass checking the rest of the modals and proceed to
   * submit changes
   */
  _isMinAdrNotMetAndBlocked () {
    let minPsvNotMet = !this.isPsvTotalHidden && this.totalsInfo.totalCartPsv < this.minAdrPsv;
    if (minPsvNotMet) {
      if (this.minAdrPsvCheck === 'block') {
        return true;
      } else if (this.minAdrPsvCheck === 'warning') {
        this.push('modalsToResolve', 'minAdrPsvModal');
      }
    }
    return false;
  }

  /**
   * Checks if the ADR's scan cards are valid by simulating the order update.
   * On a failed simulate call, triggers the display of error messages.
   * @returns {Promise<boolean>} Promise resolves `true` if check succeeds, `false` if it fails
   */
  async _checkScanCards () {
    // If ADR has no scan cards, skip this check
    if (!this.currentADP.Scan) return true;

    let orderId = this.currentADP.SalesOrderDetail.OrderID;
    let lineItemsForServiceCall = this.convertToLineItems(this.cartInfo);
    let action = '';
    let options = {
      ProductDiscountCode: this.promoCode.valid,
      ShippingDiscountCode: this.shipCode.valid,
      forceRefresh: false,
      Simulate: true
    };

    // PUT ADR with updated items, retry on adrRefresh error requesting forceRefresh
    // Error is caught on this level and not in _editAdrItems() - see method comment block
    this.scanCardDeactivateError;
    return this._editAdrItems(orderId, lineItemsForServiceCall, action, options)
      .then(() => {
        this._updateModalsToResolve('deactivateExtraScanCards');
        return true;
      })
      .catch(err => {
        if ((err instanceof NsRestError) && (err.messages.some(m => m.code === 'deactivateDueTo'))) {
          // Scan card problem found; add scan card warning modal to modals queue
          const idx = err.messages.findIndex(e => e.code === 'deactivateDueTo');
          this.scanCardDeactivateError = err.messages[idx].message;
          if (this.scanCardDeactivateError && this.modalsToResolve.indexOf('deactivateExtraScanCards') === -1) {
            this.unshift('modalsToResolve', 'deactivateExtraScanCards');
          }
          // Scan card error is handled in modal; remove from error list
          err.messages.splice(idx, 1);
        }
        // Handle remaining errors as normal
        this._handleError(err);
        return false;
      });
  }

  /**
   * Checks if pay with points warning modal needs to be displayed.
   * Modal will be shown if 'redeem points' checkbox is selected,
   * but user did not select any products to 'redeem with points'.
   * @returns {boolean} `true` if check succeeds and `false` if it fails
   */
  _checkPayWithPoints () {
    let isPayWithPoints = this.cartInfo.find(item => !item.isHidden && item.PayWithPoints);
    if (this.redeemPointsChecked && !isPayWithPoints) {
      this.push('modalsToResolve', 'payWithPointsModal');
      return false;
    }
    return true;
  }

  /**
   * Observer for modalsToResolve.
   * Handles the sequence of displaying modals to prevent multiple modals from
   * being displayed at the same time.
   */
  _handleModals () {
    // Do not move forward if modal is open or there are no modals to display (based off length of the modalsToResolve array)
    if (this.modalOpen || !this.hasModalsToDisplay) return;

    for (let item of this.modalsToResolve) {
      this.$[item].open();
      this.modalOpen = true;
      break;
    }

    // When all modals have been 'resolved' by the user, the request can then be submitted.
    if (this.modalsToResolve.length === 0) this._submitChanges();
  }

  /**
   * Calls the appropriate function based on 'shipWhen' variable.
   * options: shipImmediate, shipFuture - available when redeem points is selected.
   * shipImmediate calls _shipOneTimeOrders (sets up cart object and redirects to cart page)
   * Otherwise, set up and send the request to save the changes to the ADR.
   */
  _submitChanges () {
    let oneTimeItems = this.cartInfo.filter((i) => !i.isHidden && i.OneTime);

    if (this.shipWhen === 'shipImmediate' && oneTimeItems.length > 0) {
      this._shipOneTimeOrders();
    } else {
      this._shipWithAdr();
    }
  }

  /**
   * Function called to save ADR for future processing.
   */
  _shipWithAdr () {
    let orderId = this.currentADP.SalesOrderDetail.OrderID;
    let lineItemsForServiceCall = this.convertToLineItems(this.cartInfo);
    let action = '';
    let options = {
      ProductDiscountCode: this.promoCode.input,
      ShippingDiscountCode: this.shipCode.input,
      forceRefresh: false
    }
    // PUT ADR with updated items, retry on adrRefresh error requesting forceRefresh
    // Error is caught on this level and not in _editAdrItems() - see method comment block
    this._editAdrItems(orderId, lineItemsForServiceCall, action, options)
      .catch(err => {
        console.error(err);
        if (this._isErrorAdrRefresh(err)) {
          options.forceRefresh = true;
          return this._editAdrItems(orderId, lineItemsForServiceCall, action, options);
        } else {
          this._handleError(err);
        }
      });
  }

  /**
   * PUT call to update ADR lineItems
   * Errors returned from service are caught by the implementation of this method - see _callSaveChanges()
   * This is done so that the retry would only fire once in case the data is in a bad state returned for GET adrDetails from Server
   * @param {String} orderId - ADR Id
   * @param {[Items]} [lineItemsForServiceCall] - Array of Line Items to update
   * @param {String} [action] - action to apply to lineItem - Add/Update/Delete - already set before service is called in each element of [lineItemsForServiceCall]
   * @param {Object} [options] - object that can hold extra options i.e. forceRefresh, ProductDiscountCode, ShippingDiscountCode etc
   */
  async _editAdrItems (orderId, lineItemsForServiceCall, action, options) {
    this.showLoader = true;

    // Scan Cards object needs to be sent with the request.
    let scanCards = this.currentADP.Scan;
    if (scanCards) {
      scanCards.map(item => {
        if (item.Action === null) {
          item.Action = 'CHANGE';
        }
      });
      options.Scan = scanCards;
    }
    if (this.adjustPoints) {
      let currencyCode = CurrencyService.getCurrency(this.country).currencyCode;
      await AdrService.editAdrLoyaltyRedemption(this.adrId, 0, currencyCode);
    }
    await AdrService.editAdrItems(orderId, lineItemsForServiceCall, action, options)
      .then(() => AdrService.getAdrDetails(orderId, {forceRefresh: true}))
      .then(async (response) => {
        if (this.adjustPoints) {
          const user = UserService.getUser();
          const marketConfig = ConfigService.getMarketConfig();

          let configuration = {
              requestUrl: marketConfig.awsUrl,
              market: user.countryCd,
              userId: user.id,
              jwt: 'JWT ' + user.eid,
              client_id: marketConfig.checkout.clientId,
              client_secret: marketConfig.checkout.clientSecret
          };
          const loyaltyService = new NsLoyalty(configuration);
          await loyaltyService.getRewardsBucketsAndCurrentTierData();
          if (loyaltyService.currentTierData) {
            const maxPoints = await AdrService.getAdrLoyaltyRedemptionMax(this.adrId);
            const limit = maxPoints * loyaltyService.currentTierData.redemptionLimit;
            const maxRedemptionAmount = limit;
            const pointsToSet = this.loyaltyPointsUsed < maxRedemptionAmount? this.loyaltyPointsUsed : maxRedemptionAmount;
            let currencyCode = CurrencyService.getCurrency(this.country).currencyCode;
            await AdrService.editAdrLoyaltyRedemption(this.adrId, pointsToSet, currencyCode);
          }
        }
        // Clear cart and redirect to ADP Manage or Cart only if the request is not simulate.
        if (!options.Simulate) {
          this.hasCommittedChange = true;
          CartService.clearCart();
          this._returnToADPManageOrCart();
        }
      })
      .finally(() => {
        this.showLoader = false;
      });
  }

  /**
   * Returns boolean based on stale adr data. Adr orders are timestamped for versioning.
   * @param {String} err Error thrown from AdrService.transformFetchResponse()
   * @return {Boolean} isErrorAdrRefresh
   */
  _isErrorAdrRefresh (err) {
    return err.message.includes(this._CONSTANTS.BAD_REQUEST_CODE) && err.message.includes(this._CONSTANTS.REFRESH_EXCEPTION);
  }

  /**
   * Fetches the total points and assigns the value to the this.originalPointsBalance
   */
  async _initUserPointsBalance (forceRefresh = false) {
    if (!this.currentADP || !this.currentADP.SalesOrderDetail) {
      this.originalPointsBalance = 0;
      return;
    }

    const shippingParty = this.currentADP.SalesOrderDetail.Shipping.ShippingParty.find(party => party.Type === 'SHIP_TO');
    const shippingCountry =
      this.adrPointsCountryOverride ||
      (shippingParty
        ? shippingParty.ShippingAddress.Address.CountryCode
        : this.country
      );

    const useShippingCountry =
      this.useShippingCountryAdrPoints ||
      !!this.adrPointsCountryOverride;

    return AdrService.getMarketAdrPointsData({
      forceRefresh,
      useShippingCountry,
      shippingCountry
    })
      .then((pointsData) => {
        this.originalPointsBalance = pointsData ? pointsData.AvailablePoints : 0;
      })
      .catch(err => {
        console.error(err);
        this.originalPointsBalance = 0;
        this._handleError(err);
      });
  }

  /**
   * Sets the redeem points checkbox according to the value of the redeem points checked property in session storage.
   */
  _initializeRedeemPointsCheckbox () {
    this.redeemPointsChecked = AdrService.getRedeemPointsChecked();
    AdrService.setRedeemPointsChecked(false);
  }

  /**
   * Convert cartProducts into array of line items for PUT call
   * @param {Object} cartProducts - line items for the PUT call
   * @return cartProducts with an associated Action into array of line items
   */
  convertToLineItems (cartProducts) {
    if (cartProducts === null || cartProducts.length === 0) {
      return [];
    }

    return cartProducts
      .map((product)=> ({
        'SKU': product.SKU,
        'LineID': product.LineID,
        'ParentID': product.ParentID,
        'PromoCode': product.PromoCode,
        'Category': product.Category,
        'Custom': product.Custom,
        'Pricing': product.Pricing,
        'RequestedQuantity': product.RequestedQuantity,
        'PayWithPoints': product.PayWithPoints,
        'OneTime': product.PayWithPoints ? false: product.OneTime,
        'Action': this._assignAction(product)
      }))
      .filter(product => product.Action);
  }

  /**
   * Assigns an Action property for the PUT call, to inform the server if a line item
   * has been added or updated by the user since the initial cart.
   * An item that was added through the regular shopping cart will already have an Action of
   */
  _assignAction (lineItem) {
    // Corresponding line item from the original (unaltered) ADR record
    const originalLineItem = this.initialCart.find(item =>
      (item.LineID && item.LineID === lineItem.LineID)
    );

    // Flag indicating whether this line item was marked for deletion
    const isMarkedDelete = this.deletedItems.some(item =>
      (item.LineID && item.LineID === lineItem.LineID) ||
      (item.NewItemLineID && item.NewItemLineID === lineItem.NewItemLineID)
    );

    /*
    Handling depends on whether the given line item corresponds to an existing
    line item on the ADR.
    Altering an existing line item --> UPDATE action
    Deleting an existing line item --> DELETE action
    Unaltered existing line item --> no action
    Adding a new line item --> ADD action
    Adding and then deleting a new line item --> no action
    */
    if (originalLineItem) {
      if (isMarkedDelete) return 'DELETE';

      const hasBeenAltered =
        originalLineItem.RequestedQuantity !== lineItem.RequestedQuantity ||
        originalLineItem.PayWithPoints !== lineItem.PayWithPoints ||
        originalLineItem.OneTime !== lineItem.OneTime;

      return hasBeenAltered ? 'UPDATE' : '';
    } else {
      return !isMarkedDelete ? 'ADD' : '';
    }
  }

  /**
   * Listens for delete-line-item-not-allowed events. Prevents the user from deleting the last line item
   * in the cart.
   */
  _openDeleteLastCartItemModal () {
    this.$.deleteLastCartItemModal.open();
    this.hideOverlay = false;
  }

  /**
   * On-tap for the close button of the delete last cart item modal. Closes the modal and hides the overlay.
   */
  _closeDeleteLastCartItemModal () {
    this.$.deleteLastCartItemModal.close();
    this._hideOverlay();
  }

  /**
   * on-tap for the modal overlay. Note that hiding the overlay also removes the modal, unless you click the
   * modal close button - in that case, both steps are necessary.
   */
  _hideOverlay () {
    this.hideOverlay = true;
  }

  /**
   * Displays the promo code section if showPromoCode or showShipCode config is true.
   * @param {*} showPromoCode
   * @param {*} showShipCode
   */
  _showPromoSection(showPromoCode, showShipCode) {
    return showPromoCode || showShipCode;
  }

  _initExistingCouponData() {
    const sod = this.currentADP.SalesOrderDetail;

    const productSavings = sod.LineItemDetails
      .map(li => li.ProductPromoSavings)
      .reduce((a, x) => a + x, 0);
    this.set('productPromoSavings', productSavings);

    const shippingSavings = sod.LineItemDetails
      .map(li => li.FreightPromoSavings)
      .reduce((a, x) => a + x, 0);
    this.set('shippingPromoSavings', shippingSavings);

    // Show only the coupon code summary
    this.isPromoCpnSummaryHidden = false;
    this.isCpnMainMenuDropdownHidden = true;
    this.isCpnMainMenuHidden = true;
    this.isCpnListHidden = true;
  }

  /**
   * Retrieves available coupons from the server,
   * storing them in `this.availableCoupons`.
   */
  _initAvailableCoupons() {
    CampaignService.getAvailableCouponOffers()
      .then((couponOffers) => {
        // Show the coupon code starting point
        this.isPromoCpnSummaryHidden = true;
        this.isCpnMainMenuDropdownHidden = false;
        this.isCpnMainMenuHidden = false;
        this.isCpnListHidden = true;

        this.availableCoupons = couponOffers
          // 'order' type coupons cannot be applied to ADRs
          .filter((co) => co.orderTypeAvailability !== 'order')
          .map((co) => {
            co.expText = co.expiration
              ? moment(co.expiration).format(this.couponExpiryDateFormat)
              : '';
            return co;
          });
      })
      .catch(err => console.error(err));
  }

  _toggleCpnCollapsible() {
    const collapsible = this.shadowRoot.querySelector('#cpnCollapsible');
    collapsible.toggle();
    this.isCpnCollapsibleOpen = collapsible.opened;
    this.set('isSaveBtnDisabled', this.isCpnCollapsibleOpen);
  }

  _toggleCpnList() {
    this.set('isCpnListHidden', !this.isCpnListHidden);
  }

  _onSelectCoupon(evt) {
    const coupon = evt.model.get('coupon');
    this.selectedCpnCode = coupon.code;
    this.selectedCpnDiscount = coupon.discountAmount;
    this.isCpnMainMenuHidden = true;

    this.showLoader = true;

    this._applyDiscountCodes({
      ProductDiscountCode: coupon.code,
      ShippingDiscountCode: ''
    }, true)
      .then(() => {
        this.set('promoCode.input', coupon.code);
        this.set('promoCode.valid', coupon.code);

        this.isCpnListSelectedCpnHidden = false;
        this.isApplyListCpnBtnDisabled = false;
      })
      .finally(() => {
        this.showLoader = false;
      })
      .catch(err => {
        const composedPath = evt.composedPath();
        if (composedPath.length) {
          composedPath[0].checked = false;
        } else if (evt.originalTarget) {
          evt.originalTarget.checked = false;
        }
        this._handleError(err);
      });
  }

  _resetDiscountCodeUI() {
    this.isPromoCpnSummaryHidden = true;
    this.isCpnMainMenuDropdownHidden = false;
    this.isCpnCollapsibleOpen = false;
    this.isCpnMainMenuHidden = true;
    this.isCpnListHidden = true;
    this.isCpnListSelectedCpnHidden = true;
    this.isApplyListCpnBtnDisabled = true;
    this.isSaveBtnDisabled = false;
    const collapsible = this.shadowRoot.querySelector('#cpnCollapsible');
    collapsible.hide();
  }

  _onTapClearCpn() {
    this.showLoader = true;

    this._clearDiscountCodes()
      .then(() => this._resetDiscountCodeUI())
      .finally(() => {
        this.showLoader = false;
      })
      .catch(e => {
        this._handleError(e)
      });
  }

  _onTapApplyListCpn() {
    if (this.isApplyListCpnBtnDisabled) return;

    // Show only the coupon code summary
    this.isPromoCpnSummaryHidden = false;
    this.isCpnMainMenuDropdownHidden = true;
    this.isCpnCollapsibleOpen = false;
    this.isCpnMainMenuHidden = true;
    this.isCpnListHidden = true;
    this.isCpnListSelectedCpnHidden = true;
    this.isApplyListCpnBtnDisabled = true;
    this.isSaveBtnDisabled = false;
    const collapsible = this.shadowRoot.querySelector('#cpnCollapsible');
    collapsible.hide();
  }

  _onTapCancelListCpn() {
    if (!this.promoCode.input && !this.promoCode.valid) {
      this._resetDiscountCodeUI();
      return;
    }

    this.showLoader = true;

    this._clearDiscountCodes()
      .then(() => this._resetDiscountCodeUI())
      .finally(() => {
        this.showLoader = false;
      })
      .catch(e => {
        this._handleError(e)
      });
  }

  /**
   * Call simulate adr update to determine if the promo code is valid.
   * @param {*} e
   */
   _onTapApplyDiscountCode(e) {
    this.showLoader = true;
    this.discountErrMsg = '';
    let discountCodeKey = e.target.dataset.discountKey;
    let discountCodeValue = e.target.dataset.discountValue;
    let options = {'ProductDiscountCode': '', 'ShippingDiscountCode': '', 'Simulate': true}
    options[discountCodeKey] = this[discountCodeValue].input ? this[discountCodeValue].input.toUpperCase() : '';

    this._applyDiscountCodes(options, true)
      .then((data) => {
        this.set(discountCodeValue + '.valid', data.SalesOrderDetail[discountCodeKey]);

        // Show only the coupon code summary
        this.isPromoCpnSummaryHidden = false;
        this.isCpnMainMenuDropdownHidden = true;
        this.isCpnCollapsibleOpen = false;
        this.isCpnMainMenuHidden = true;
        this.isCpnListHidden = true;
        this.isCpnListSelectedCpnHidden = true;
        this.isApplyListCpnBtnDisabled = true;
        this.isSaveBtnDisabled = false;

        this.showLoader = false;
      })
      .catch((e) => {
        this.set(discountCodeValue + '.input', '');
        this._handleError(e)
      });
  }

  /**
   *
   * @param {{
   * ProductDiscountCode?: string
   * ShippingDiscountCode?: string
   * }} codes
   * @param {boolean} simulate
   */
  async _applyDiscountCodes(codes, simulate) {
    let lineItemsForServiceCall = this.convertToLineItems(this.cartInfo);
    const options = Object.assign({}, codes, {Simulate: simulate});
    return AdrService
      .updateAdrItems(this.adrId, lineItemsForServiceCall, options)
      .then(data => {
        const productSavings = data.SalesOrderDetail.LineItemDetails
          .map(li => li.ProductPromoSavings)
          .reduce((a, x) => a + x, 0);
        this.set('productPromoSavings', productSavings);

        const shippingSavings = data.SalesOrderDetail.LineItemDetails
          .map(li => li.FreightPromoSavings)
          .reduce((a, x) => a + x, 0);
        this.set('shippingPromoSavings', shippingSavings);
        this.set('totalsInfo.totalCartPrice', data.SalesOrderDetail.OrderTotals.GrandTotal);
        this.set('totalsInfo.totalCartPsv', data.SalesOrderDetail.OrderTotals.PSV);
        return data;
      });
  }

  async _clearDiscountCodes() {
    return this._applyDiscountCodes({
      ProductDiscountCode: '',
      ShippingDiscountCode: ''
    }, true)
      .then(() => this._initAvailableCoupons())
      .then(() => {
        this.set('promoCode', {});
        this.set('shipCode', {});
      });
  }

  /**
   * Set promoCode or shipCode to empty object, and reset discountErrMsg to empty string.
   * @param {*} e
   */
  _removeDiscountCode (e) {
    this.showLoader = true;
    this.discountErrMsg = '';
    this[e.target.dataset.discountValue] = {}
    this.showLoader = false;
  }

  /**
   * Computed method returns value for purchaseWithPointsCounterLabel property
   * @param {Number} [purchaseWithPointsCounter] - property bounded to line item, updates as line-item updates
   * @param {String} [purchaseCounterLabel] - label from translations
   * @return {String} PurchaseWithPointsCounterLabel - String concat of purchaseWithPointsCounter and purchaseCounterLabel
   */
  _computePurchaseWithPointsCounterLabel (purchaseWithPointsCounter, purchaseCounterLabel) {
    if (!purchaseCounterLabel) return '';

    if (purchaseCounterLabel.includes('{qty}'))
      return `(${purchaseCounterLabel.replace('{qty}', purchaseWithPointsCounter)})`;

    return `(${purchaseWithPointsCounter} ${purchaseCounterLabel})`;
  }

  /**
   * @return {boolean} Whether or not the points section should be displayed.
   */
  _showPointsSection (enablePayWithPoints, originalPointsBalance) {
    return enablePayWithPoints && originalPointsBalance;
  }

  _handleError (err) {
    if (err instanceof NsRestError) {
      err.messages.forEach(m => {
        console.error(m.technicalMessage, m);
        this._addToastMessage('error', { header: m.message, scrollIntoView: true });
      });
    } else {
      console.error(err);
      this._addToastMessage('error', { header: err.message, scrollIntoView: true });
    }

    this.showLoader = false;
  }

  /**
   * Function called to redeem points and ship ADR immediately.
   */
  _shipOneTimeOrders () {
    this.showLoader = true;
    CartService.clearCart();
    const ORDER_REASON_STANDARD = 'STANDARD';
    const ORDER_REASON_ADR_OVERRIDE = 'ADR_OVERRIDE';
    let oneTimeItems = this.cartInfo.filter(item => !item.isHidden && item.OneTime);
    const orderReason = this.shipOneTimeImmediateOrderType === 'override' ? ORDER_REASON_ADR_OVERRIDE : ORDER_REASON_STANDARD;

    storage.setItem(storage.metadata.ADR_OVERRIDE_ID, this.adrId, {});
    storage.setItem(storage.metadata.ORDER_REASON, orderReason, {});
    storage.setItem(storage.metadata.ADR_ONE_TIME_ITEMS, oneTimeItems);

    const oneTimeItemPromises = oneTimeItems.map((item) =>
      CartService.addSkuToCart({
        'sku': item.SKU,
        'userId': user.id,
        'cntryCd': this.country,
        'language': this.language,
        'qty': item.RequestedQuantity,
        'redeem': item.PayWithPoints
      }));

    Promise.all(oneTimeItemPromises).then(() => {
      CartService.goToCartPage();
    });
  }

  /**
   * Closes pay with points modal.
   */
  _closePayWithPointsModal () {
    this.$.payWithPointsModal.close();
    this._updateModalsToResolve();
  }

  _resolvePayWithPointsModal () {
    this.$.payWithPointsModal.close();
    this._updateModalsToResolve('payWithPointsModal');
  }

  _updateModalsToResolve (modalName) {
    this._hideOverlay();
    this.modalOpen = false;
    if (modalName) {
      let index = this.modalsToResolve.indexOf(modalName);
      if (index >= 0) this.splice('modalsToResolve', index, 1);
    } else {
      this.hasModalsToDisplay = false;
      this.set('modalsToResolve', []);
    }
  }

  /**
   * Observer for redeem points shipping radio button selection
   * Sets the label for the save button
   * @params (String) shipWhen
   */
  shipWhenSelectionChanged (shipWhen) {
    this.set('saveLabel', shipWhen === 'shipImmediate' ? this.adrCartSaveShipImmediateLabel : this.adrCartSaveLabel);
  }

  /**
   * Observer for search bar placeholder translation.
   * Creates and assigns the translations object that will be passed on to search.
   */
  _searchbarPlaceholderTextChanged () {
    this.set('searchBarPlaceholderTranslations', {
      ...this.searchBarPlaceholderTranslations,
      desktopPlaceholder: this.searchbarPlaceholderText
    });
  }

  /**
   * Observer for search bar placeholder translation.
   * Creates and assigns the translations object that will be passed on to search.
   */
  _searchbarCancelTextChanged () {
    this.set('searchBarPlaceholderTranslations', {
      ...this.searchBarPlaceholderTranslations,
      cancel: this.searchbarCancelText
    });
  }

  /**
   * Adds a toast message for display to the user.
   * @param {string} type - message type: "success", "warning", "error", "information"
   * @param {Object} options - options:
   * @param {string} [options.header] - message header text
   * @param {string} [options.message] - message body text
   * @param {boolean} [options.scrollIntoView = false] - flag indicating whether to scroll to the toast message
   * @param {boolean} [options.showUndoOption = false] - flag indicating whether to show a print button
   */
  _addToastMessage(type, options = {}) {
    const MESSAGE_DEFAULTS = {
      type: 'information',
      header: '',
      scrollIntoView: false,
      showUndoOption: false
    };
    const messageObject = Object.assign({}, MESSAGE_DEFAULTS, options, {type});
    this.push('toastMessages', messageObject);
  }

  /**
   * Clears all toast messages from the page.
   */
  _clearToastMessages() {
    this.set('toastMessages', []);
  }

  /**
   * Observer to changes in the toast messages collection.
   * This observer triggers scrolling to the toast messages section if a
   * toast message is added with the `scrollIntoView` flag set.
   * @param {*} changeRecord - change record of the mutations performed on the observed array.
   */
  _onToastMessagesChanged(changeRecord) {
    if (!changeRecord) return;

    /*
    Scrolling the page to the toast messages section should only be triggered if
    one or more toast messages have been added to the collection, and one or more
    of those added messages have the scrollIntoView flag enabled.
    */
    const shouldScrollToToasts = changeRecord.indexSplices.some(iSplice => {
      return iSplice.object
        .slice(iSplice.index, iSplice.index + iSplice.addedCount)
        .some(toast => toast.scrollIntoView);
    });

    if (shouldScrollToToasts) {
      this.shadowRoot.querySelector('#toastMessages').scrollIntoView({behavior: 'smooth'});
    }
  }

  /**
   * Closes minAdrPvModal if the close button is clicked
   */
  _closeMinAdrPsvModal () {
    this.$.minAdrPsvModal.close();
    this._updateModalsToResolve();
  }

  /**
   * on-tap function for minimumAdrPsvModal save button.
   * closes the modal and remove the minAdrPsvModal from modalsToResolve array
   */
  _resolveMinAdrPsvModal () {
    this.$.minAdrPsvModal.close();
    this._updateModalsToResolve('minAdrPsvModal');
  }

 /**
   * Closes deactivateExtraScanCardsModal if the close or cancel button is clicked
   */
  _closeDeactivateExtraScanCards () {
    this.$.deactivateExtraScanCards.close();
    this._updateModalsToResolve();
  }

  /**
   * on-tap function for deactivateExtraScanCardsModal deactivate button.
   * closes the modal and rechecks if number of scan cards saved is no longer in excess.
   */
  _resolveDeactivateExtraScanCards (e) {
    this.$.deactivateExtraScanCards.close();
    this.modalOpen = false;
    this._checkScanCards();
  }

  /**
   * Observer for deactivate checkbox in deactivateExtraScanCardsModal.
   * sets scan card request body action to deactivate.
   */
  _onChecked (e) {
    let scanCard = this.currentADP.Scan.find(item => item.ScanNumber === e.model.__data.item.ScanNumber)
    if (e.target.checked) {
      scanCard.Action = 'DEACTIVATE';
      scanCard.Active = false;
    } else {
      scanCard.Action = null;
      scanCard.Active = true;
    }
  }

  _computeUsingPoints(currentADP) {
    return Math.abs(currentADP.SalesOrderDetail.OrderTotals.PointsUsed);
  }

  _computeAdjustPoints(removedItems, usingPoints) {
    return removedItems && (usingPoints > 0);
  }

  /**
   * @param {string} baseText
   * @param {any[]} coupons
   */
  _computeOpenCpnListBtnTxt(baseText, coupons) {
    return (baseText || '{count} Coupons Available').replace(/{count}/g, coupons.length);
  }

  _computeCpnCollapseIcon(isCollapsibleOpen) {
    return isCollapsibleOpen ? 'expand-less' : 'expand-more';
  }

  _computeIsCpnListEmpty(coupons) {
    return coupons.length === 0;
  }

  /**
   *
   * @param {number} n
   * @param {number} places
   * @returns
   */
  _toRoundedString(n, places = 0) {
    return n.toFixed(places);
  }
  async _getAgelocMeAssessments() {
    let _this = this;
    let runConfig = RunConfigService.getRunConfig();
    const localeCode = `${runConfig.language || "en"}_${runConfig.country || "US"}`;

    const siteUrl = window.location.origin.startsWith("http://localhost")
      ? runConfig.baseUrl || "https://test.nuskin.com"
      : window.location.origin;

    const serviceData = await MarketHeaderService.getMarketHeaderData(
      `/content/nuskin/${localeCode}/home.html`,
      siteUrl,
      false,
      "searchbar"
    );

    let _getAssessments = AssessmentService.getAssessmentsV2;

    await _getAssessments(
      function success(data) {
        if (data.assessments) {
          _this.assessments = data.assessments;
        }
      },
      function failed(data) {}
    );
  }

  _createAgelocCustomLabel(product) {
    const defaultString = '{ title } { code } <br/> { label }';
    let label = this.agelocMeCustomLabel || defaultString
    return label.replace(/{ title }/, product.title)
          .replace(/{ label }/, product.agelocme.label)
          .replace(/{ code }/, product.agelocme.code);
  }

}

customElements.define(NsAdpCart.is, NsAdpCart);
